import { INVENTORY_STATUSES } from 'constants/statuses'

/**
 * ONE source for "what colour is this tag SKU". Extracted 2026-08-22 because the same
 * three-line derivation had been hand-rolled THREE times — the status column
 * (`Inventory.tsx`), the level gauge (`Cells.tsx` `InventoryLevelCell`), and the detail
 * page (`TagInventoryPage.tsx`) — which is exactly the second-hand-roll trigger in
 * working-conventions §36. They had already drifted in the way that matters: the gauge's
 * copy seeded `status` as the empty string and let the three `if`s fall through, so a
 * non-finite capacity left `status === ''`, and the very next line indexed
 * `INVENTORY_STATUSES[''].bar` — a TypeError that takes the whole table down through the
 * error boundary. The other two copies returned a real status on every path.
 *
 * So these helpers are TOTAL by construction: every input maps to one of the three keys,
 * and every lookup goes through `inventoryStatusMeta`, which cannot return undefined.
 */

export type InventoryStatus = keyof typeof INVENTORY_STATUSES

export interface InventoryLike {
  capacity?: number | null
  available_qty?: number | null
  uncertified_qty?: number | null
  threshold?: number | null
}

/**
 * `?? 0` does NOT catch NaN — `NaN ?? 0` is NaN — which is how a bad number propagated all
 * the way into MUI's `translateX(NaN%)` and into the status fall-through above. Every read
 * of a numeric field goes through here so a non-number is neutralised at the boundary.
 */
const finite = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback

/**
 * CAPACITY = Available − demand: tags in hand, minus in-stock units still waiting for one.
 * Prefers the server-computed `capacity` and recomputes only when it is missing or
 * unusable, so a caller that has not been migrated renders the same number rather than a
 * silent NaN.
 *
 * Deliberately SIGNED. A negative value is a shortfall ("short by 26"), which is the
 * actionable number on this surface and the one the Capacity column is sorted by; flooring
 * it at 0 would collapse every short SKU into one indistinguishable bucket and destroy the
 * "who is worst off" sort. The company-wide card floors per SKU for a different and equally
 * deliberate reason — see `get-tag-inventory-overview.use-case.ts`.
 */
export const inventoryCapacity = (row?: InventoryLike | null): number => {
  const stated = finite(row?.capacity, Number.NaN)
  if (Number.isFinite(stated)) return stated
  return finite(row?.available_qty) - finite(row?.uncertified_qty)
}

/**
 * Capacity vs the alert threshold, with `critical` winning. Split from `inventoryStatus`
 * so the gauge can pass the capacity it has ALREADY resolved (it falls back to summing
 * `for_products` when `uncertified_qty` is absent, a fallback the row shape here cannot
 * express) without the two disagreeing about the arithmetic.
 */
export const inventoryStatusFromCapacity = (
  capacity: unknown,
  threshold: unknown,
): InventoryStatus => {
  const value = finite(capacity, Number.NaN)
  // Non-finite capacity is a data fault, not a level. Report it as `critical` rather than
  // guessing: under-reporting a shortage costs a missed reorder, over-reporting costs a
  // glance at a row that turns out to be fine. Auto-ordering is decided server-side, so a
  // loud UI default cannot trigger a purchase.
  if (!Number.isFinite(value)) return 'critical'
  if (value <= 0) return 'critical'
  return value > finite(threshold) ? 'plenty' : 'low'
}

export const inventoryStatus = (row?: InventoryLike | null): InventoryStatus =>
  inventoryStatusFromCapacity(inventoryCapacity(row), row?.threshold)

/**
 * The only sanctioned way to read the palette entry. Indexing `INVENTORY_STATUSES`
 * directly is what threw; this cannot.
 */
export const inventoryStatusMeta = (status?: string) =>
  INVENTORY_STATUSES[status as InventoryStatus] ?? INVENTORY_STATUSES.critical
